import { create } from 'zustand';
import { CombatEvent, ChatMessage, ProjectileState, RoomMetadata, TankState, UserProfile } from '@tankworld/shared';
import { soundSystem } from '../audio/sound-system.js';


export type GameMode = 'menu' | 'singleplayer' | 'multiplayer';
export type CameraMode = 'third_person' | 'commander' | 'sniper';
export type BotDifficulty = 'easy' | 'normal' | 'hard';

export interface GameSettings {
  audioVolume: number;
  mouseSensitivity: number;
  invertY: boolean;
  shadows: boolean;
  graphicsQuality: 'low' | 'medium' | 'high';
  touchControls: boolean;
}


interface GameStoreState {
  // Authentication
  user: UserProfile | null;
  token: string | null;
  isAuthModalOpen: boolean;
  authModalMode: 'login' | 'register';
  openAuthModal: (mode?: 'login' | 'register') => void;
  closeAuthModal: () => void;
  login: (usernameOrEmail: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (username: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  checkAuth: () => Promise<void>;

  // Screen & Mode
  gameMode: GameMode;
  isPaused: boolean;
  cameraMode: CameraMode;
  botDifficulty: BotDifficulty;

  // Identity & Tank
  playerName: string;
  selectedTankClass: string;
  localPlayerId: string | null;

  // World State
  tanks: TankState[];
  projectiles: ProjectileState[];
  combatEvents: CombatEvent[];
  activeRoom: RoomMetadata | null;
  countdownRemaining: number | null;
  chatMessages: ChatMessage[];

  // Telemetry for HUD
  speedKmh: number;
  health: number;
  maxHealth: number;
  isAlive: boolean;
  reloadProgress: number;
  hullHeadingDeg: number;
  turretHeadingDeg: number;
  gunElevationDeg: number;
  kills: number;
  deaths: number;
  score: number;

  // Diagnostics
  fps: number;
  pingMs: number;
  serverTick: number;

  // Settings
  settings: GameSettings;

  // Actions
  setGameMode: (mode: GameMode) => void;
  setPaused: (paused: boolean) => void;
  cycleCameraMode: () => void;
  setCameraMode: (mode: CameraMode) => void;
  setPlayerName: (name: string) => void;
  setSelectedTankClass: (cls: string) => void;
  setBotDifficulty: (diff: BotDifficulty) => void;
  setLocalPlayerId: (id: string | null) => void;
  setActiveRoom: (room: RoomMetadata | null) => void;
  setCountdown: (seconds: number | null) => void;
  addChatMessage: (chat: ChatMessage) => void;
  clearChatMessages: () => void;
  updateWorldSnapshot: (tanks: TankState[], projectiles: ProjectileState[], events: CombatEvent[], tick?: number) => void;
  updateDiagnostics: (fps: number, pingMs: number, tick?: number) => void;
  updateSettings: (newSettings: Partial<GameSettings>) => void;
  resetGame: () => void;
}

export const useGameStore = create<GameStoreState>((set, get) => ({
  // Authentication state
  user: null,
  token: typeof window !== 'undefined' ? localStorage.getItem('tankworld_auth_token') : null,
  isAuthModalOpen: false,
  authModalMode: 'login',

  openAuthModal: (mode = 'login') => {
    soundSystem.playUiClick();
    set({ isAuthModalOpen: true, authModalMode: mode });
  },

  closeAuthModal: () => {
    soundSystem.playUiClick();
    set({ isAuthModalOpen: false });
  },

  login: async (usernameOrEmail, password) => {
    try {
      const res = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernameOrEmail, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        return { success: false, error: data.error || 'Đăng nhập thất bại' };
      }

      if (typeof window !== 'undefined') {
        localStorage.setItem('tankworld_auth_token', data.token);
      }

      set({
        user: data.user,
        token: data.token,
        playerName: data.user.username,
        isAuthModalOpen: false,
      });

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Lỗi kết nối máy chủ' };
    }
  },

  register: async (username, email, password) => {
    try {
      const res = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        return { success: false, error: data.error || 'Đăng ký thất bại' };
      }

      if (typeof window !== 'undefined') {
        localStorage.setItem('tankworld_auth_token', data.token);
      }

      set({
        user: data.user,
        token: data.token,
        playerName: data.user.username,
        isAuthModalOpen: false,
      });

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Lỗi kết nối máy chủ' };
    }
  },

  logout: () => {
    soundSystem.playUiClick();
    if (typeof window !== 'undefined') {
      localStorage.removeItem('tankworld_auth_token');
    }
    set({
      user: null,
      token: null,
      playerName: 'Commander_' + Math.floor(100 + Math.random() * 900),
    });
  },

  checkAuth: async () => {
    if (typeof window === 'undefined') return;
    const token = localStorage.getItem('tankworld_auth_token');
    if (!token) return;

    try {
      const res = await fetch('/api/v1/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.user) {
          set({
            user: data.user,
            token,
            playerName: data.user.username,
          });
        } else {
          localStorage.removeItem('tankworld_auth_token');
          set({ user: null, token: null });
        }
      } else {
        localStorage.removeItem('tankworld_auth_token');
        set({ user: null, token: null });
      }
    } catch {
      // Offline fallback
    }
  },

  gameMode: 'menu',
  isPaused: false,
  cameraMode: 'third_person',
  botDifficulty: 'normal',


  playerName: 'Commander_' + Math.floor(100 + Math.random() * 900),
  selectedTankClass: 'mbt_standard',
  localPlayerId: null,

  tanks: [],
  projectiles: [],
  combatEvents: [],
  activeRoom: null,
  countdownRemaining: null,
  chatMessages: [],

  speedKmh: 0,
  health: 100,
  maxHealth: 100,
  isAlive: true,
  reloadProgress: 1.0,
  hullHeadingDeg: 0,
  turretHeadingDeg: 0,
  gunElevationDeg: 0,
  kills: 0,
  deaths: 0,
  score: 0,

  fps: 60,
  pingMs: 0,
  serverTick: 0,

  settings: {
    audioVolume: 0.8,
    mouseSensitivity: 1.0,
    invertY: false,
    shadows: true,
    graphicsQuality: 'high',
    touchControls: typeof window !== 'undefined' ? ('ontouchstart' in window || navigator.maxTouchPoints > 0) : true,
  },


  setGameMode: (gameMode) => {
    if (gameMode !== 'menu') {
      soundSystem.startEngine();
    } else {
      soundSystem.stopEngine();
    }
    set({ gameMode, isPaused: false });
  },

  setPaused: (isPaused) => set({ isPaused }),

  cycleCameraMode: () => {
    const current = get().cameraMode;
    const next: CameraMode =
      current === 'third_person' ? 'commander' :
      current === 'commander' ? 'sniper' : 'third_person';
    set({ cameraMode: next });
    soundSystem.playUiClick();
  },

  setCameraMode: (cameraMode) => set({ cameraMode }),
  setPlayerName: (playerName) => set({ playerName }),
  setSelectedTankClass: (selectedTankClass) => set({ selectedTankClass }),
  setBotDifficulty: (botDifficulty) => set({ botDifficulty }),
  setLocalPlayerId: (localPlayerId) => set({ localPlayerId }),
  setActiveRoom: (activeRoom) => set({ activeRoom }),
  setCountdown: (countdownRemaining) => set({ countdownRemaining }),
  addChatMessage: (chat) =>
    set((s) => ({ chatMessages: [...s.chatMessages.slice(-49), chat] })),
  clearChatMessages: () => set({ chatMessages: [] }),

  updateWorldSnapshot: (tanks, projectiles, events, tick) => {
    const { localPlayerId, combatEvents } = get();
    const localTank = tanks.find(t => t.id === localPlayerId);

    // Audio triggers for events
    for (const evt of events) {
      if (evt.type === 'fire') {
        soundSystem.playCannonBlast();
      } else if (evt.type === 'hit') {
        soundSystem.playImpact();
      } else if (evt.type === 'kill') {
        soundSystem.playExplosion();
      }
    }

    // Engine sound modulation based on speed
    if (localTank) {
      soundSystem.updateEngine(localTank.speed / 14.0);
    }

    const updatedEvents = events.length > 0 ? [...events, ...combatEvents].slice(0, 10) : combatEvents;

    set({
      tanks,
      projectiles,
      combatEvents: updatedEvents,
      serverTick: tick !== undefined ? tick : get().serverTick,
      ...(localTank ? {
        speedKmh: Math.round(Math.abs(localTank.speed) * 3.6),
        health: localTank.health,
        maxHealth: localTank.maxHealth,
        isAlive: localTank.isAlive,
        reloadProgress: localTank.reloadProgress,
        hullHeadingDeg: Math.round(((localTank.hullYaw * 180 / Math.PI) % 360 + 360) % 360),
        turretHeadingDeg: Math.round(((localTank.turretYaw * 180 / Math.PI) % 360 + 360) % 360),
        gunElevationDeg: Math.round(localTank.gunPitch * 180 / Math.PI),
        kills: localTank.kills,
        deaths: localTank.deaths,
        score: localTank.score,
      } : {}),
    });
  },

  updateDiagnostics: (fps, pingMs, tick) => set(s => ({
    fps,
    pingMs,
    serverTick: tick !== undefined ? tick : s.serverTick,
  })),

  updateSettings: (newSettings) => set(s => {
    const combined = { ...s.settings, ...newSettings };
    soundSystem.setVolume(combined.audioVolume);
    return { settings: combined };
  }),

  resetGame: () => {
    set({
      tanks: [],
      projectiles: [],
      combatEvents: [],
      speedKmh: 0,
      health: 100,
      isAlive: true,
      reloadProgress: 1.0,
      kills: 0,
      deaths: 0,
      score: 0,
      isPaused: false,
    });
  },
}));
